
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import { ResellerPlanDetails } from '@/hooks/license/resellerPlans/types';
import { usePlanPricing } from './usePlanPricing';
import BillingCycleSelector from './BillingCycleSelector';
import InvoicePreview from './InvoicePreview';
import PaymentMethodSection from './PaymentMethodSection';
import { useStripePayment } from '@/hooks/stripe/useStripePayment';

// Create a union type that can be either SubscriptionPlan or ResellerPlanDetails
type PlanType = SubscriptionPlan | ResellerPlanDetails;

interface PlanUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: PlanType | null;
  targetPlan: PlanType | null;
  onProcessPayment: () => void;
  isProcessing: boolean;
  children?: React.ReactNode; // Add children prop for plan selection dropdown
}

const PlanUpgradeDialog: React.FC<PlanUpgradeDialogProps> = ({
  open,
  onOpenChange,
  currentPlan,
  targetPlan,
  onProcessPayment,
  isProcessing,
  children
}) => {
  const {
    billingCycle,
    setBillingCycle,
    newAmount,
    prorationAmount,
    remainingDays,
    finalAmount
  } = usePlanPricing(currentPlan, targetPlan);
  
  const { processPayment } = useStripePayment();
  
  const handlePayment = async () => {
    if (!currentPlan || !targetPlan) return;
    
    try {
      // In a real app, you would create a payment method with the card details
      // For demo purposes, we'll use a test payment method ID
      const paymentMethodId = 'pm_card_visa';
      
      // Determine if this is a reseller plan or subscription plan
      const isResellerPlan = 'planType' in targetPlan && targetPlan.planType === 'reseller';
      
      // Process the payment with Stripe
      const result = await processPayment(paymentMethodId, {
        amount: finalAmount * 100, // Convert to cents for Stripe
        currency: 'usd',
        description: `Upgrade from ${currentPlan.name} to ${targetPlan.name} (${billingCycle})`,
        planId: typeof targetPlan.id === 'string' ? targetPlan.id : undefined,
        isSubscription: !isResellerPlan,
        billingPeriod: billingCycle,
        metadata: {
          previousPlan: currentPlan.name,
          newPlan: targetPlan.name,
          prorationAmount: prorationAmount.toString(),
          billingCycle
        }
      });
      
      if (result.success) {
        // Call the parent component's handler
        onProcessPayment();
      }
    } catch (error) {
      console.error('Error processing plan upgrade payment:', error);
    }
  };
  
  if (!currentPlan || !targetPlan) {
    return null;
  }
  
  return (
    <DialogContent className="sm:max-w-[600px] bg-gradient-to-b from-white to-blue-50 border-blue-200">
      <DialogHeader>
        <DialogTitle className="text-xl text-blue-800">Upgrade Your Plan</DialogTitle>
        <DialogDescription>
          Review your plan change from <span className="font-medium">{currentPlan.name}</span> to <span className="font-medium text-blue-700">{targetPlan.name}</span>
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        {/* Render the plan selection dropdown if provided */}
        {children}
        
        <BillingCycleSelector 
          billingCycle={billingCycle}
          onBillingCycleChange={setBillingCycle}
        />
        
        <InvoicePreview
          targetPlanName={targetPlan.name}
          billingCycle={billingCycle}
          newAmount={newAmount}
          prorationAmount={prorationAmount}
          remainingDays={remainingDays}
          currentPlanName={currentPlan.name}
          finalAmount={finalAmount}
        />
        
        <PaymentMethodSection finalAmount={finalAmount} />
      </div>
      
      <DialogFooter className="space-x-3">
        <Button 
          variant="outline" 
          onClick={() => onOpenChange(false)}
          className="border-gray-300 hover:bg-gray-50 hover:text-gray-700"
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button 
          onClick={handlePayment} 
          disabled={isProcessing} 
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isProcessing ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
              Processing...
            </>
          ) : (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Confirm Upgrade
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default PlanUpgradeDialog;
